// components/seo/JsonLdClient.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function readCookie(name) {
    const m = document.cookie.match(
        new RegExp("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
    );
    return m ? decodeURIComponent(m.pop()) : undefined;
}

function readNonce() {
    // 1) cookie (our main path)
    const fromCookie = readCookie("csp_nonce");
    if (fromCookie) return fromCookie;
    // 2) optional meta fallback
    const meta = document.querySelector('meta[name="x-nonce"]');
    return meta?.getAttribute("content") || undefined;
}

// Safer JSON for inline <script> tags
function safeSerialize(obj) {
    return JSON.stringify(obj)
        .replace(/</g, "\\u003c") // avoid closing </script>
        .replace(/\u2028/g, "\\u2028") // line separator
        .replace(/\u2029/g, "\\u2029"); // paragraph separator
}

export default function JsonLdClient({ json, id = "jsonld" }) {
    const [ready, setReady] = useState(false);
    const last = useRef("");

    useEffect(() => setReady(true), []);

    // normalize input
    const arr = useMemo(
        () => (Array.isArray(json) ? json : json ? [json] : []),
        [json]
    );
    const text = useMemo(
        () => arr.map((e) => safeSerialize(e)).join("\n"),
        [arr]
    );

    if (!json || !ready) return null;

    const nonce = readNonce();
    if (!nonce) return null; // no nonce => skip to avoid CSP violation

    // avoid re-creating identical nodes
    if (text === last.current) return null;
    last.current = text;

    const owner = `${id}-owner`;

    const scripts = arr.map((entry, i) => (
        <script
            key={`${id}-${i}-${text.length}`} // key shifts when content changes
            id={`${id}-${i}`}
            type="application/ld+json"
            data-owner={owner}
            nonce={nonce}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: safeSerialize(entry) }}
            suppressHydrationWarning
        />
    ));

    return createPortal(scripts, document.head);
}
